import { WavesLedger, pathForAccount, WAVES_MAINNET_CODE } from './waves-ledger.js';
import {
  transfer,
  lease as leaseTx,
  cancelLease as cancelLeaseTx,
  serialize,
  verify,
} from '@waves/waves-transactions';

// ---- surface any load-time/runtime errors on the page itself ----
function onPageError(msg) {
  let box = document.getElementById('error');
  if (!box) return;
  box.textContent = '⚠️ ' + msg;
  box.className = 'error';
}
window.addEventListener('error', (e) => onPageError('JS error: ' + (e.message || e.error)));
window.addEventListener('unhandledrejection', (e) =>
  onPageError('Async error: ' + ((e.reason && e.reason.message) || e.reason || '')),
);
document.addEventListener('DOMContentLoaded', () => {
  const s = document.getElementById('status');
  if (s) s.textContent = 'JS ready — click Connect Ledger';
});

const NODE_URL = 'https://nodes.wavesnodes.com';
const FRACTION = 1e8;
const TRANSFER_FEE = 100000; // 0.001 WAVES

const ledger = new WavesLedger(WAVES_MAINNET_CODE);
let current = { index: 0, address: null, publicKey: null };
let tokenList = []; // [{id, name, ticker, decimals, balance}]

const $ = (id) => document.getElementById(id);
const el = (id) => $(id);

/* ---------------- helpers ---------------- */
function show(id, html, cls) {
  const node = el(id);
  node.innerHTML = html;
  node.className = cls || '';
}
function showError(msg) {
  show('error', `⚠️ ${msg}`, 'error');
  el('result').className = 'result hidden';
}
function showResult(html) {
  show('result', html, 'result');
  el('error').className = 'error hidden';
}
function clearMsg() {
  el('error').className = 'error hidden';
  el('result').className = 'result hidden';
}
function fmt(units, precision = 8) {
  return (Number(units) / 10 ** precision).toFixed(precision).replace(/\.?0+$/, '') || '0';
}
async function jget(url) {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`Network error ${r.status}`);
  return r.json();
}
async function jpost(url, body) {
  const r = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const text = await r.text();
  if (!r.ok) throw new Error(`Node rejected (${r.status}): ${text}`);
  return JSON.parse(text);
}

/* ---------------- tabs ---------------- */
document.querySelectorAll('.tab').forEach((btn) => {
  btn.addEventListener('click', () => {
    document.querySelectorAll('.tab').forEach((b) => b.classList.remove('active'));
    document.querySelectorAll('.tab-panel').forEach((p) => p.classList.remove('active'));
    btn.classList.add('active');
    el('tab-' + btn.dataset.tab).classList.add('active');
    clearMsg();
  });
});

/* ---------------- connect + account ---------------- */
async function connect() {
  clearMsg();
  el('status').textContent = 'Connecting…';
  el('connect-btn').disabled = true;
  try {
    await ledger.connect();
    el('status').textContent = 'Connected ✓';
    el('tabs').classList.remove('hidden');
    await loadAccount();
  } catch (e) {
    el('status').textContent = 'Connection failed';
    showError(e.message || String(e));
  } finally {
    el('connect-btn').disabled = false;
  }
}

async function loadAccount() {
  clearMsg();
  current.index = Number(el('account-input').value) || 0;
  el('address').textContent = 'loading…';
  el('balance').textContent = '…';
  el('effective').textContent = '…';
  el('leased').textContent = '…';
  el('tokens').textContent = 'loading tokens…';
  el('leases').textContent = 'loading leases…';
  try {
    const path = pathForAccount(current.index);
    const info = await ledger.getWalletPublicKey(path);
    current.address = info.address;
    current.publicKey = info.publicKey;
    el('address').textContent = info.address;

    const [reg, eff, assets, leases] = await Promise.all([
      jget(`${NODE_URL}/addresses/balance/${info.address}`),
      jget(`${NODE_URL}/addresses/effectiveBalance/${info.address}`),
      jget(`${NODE_URL}/assets/balance/${info.address}`).catch(() => ({ balances: [] })),
      jget(`${NODE_URL}/leasing/active/${info.address}`).catch(() => []),
    ]);
    el('balance').textContent = `${fmt(reg.balance)} WAVES`;
    el('effective').textContent = `${fmt(eff.balance)} WAVES`;
    el('leased').textContent = `${fmt(reg.balance - eff.balance)} WAVES`;

    await renderTokens(assets.balances || []);
    renderLeases(leases || []);
    populateAssetDropdown();
  } catch (e) {
    showError(e.message || String(e));
  }
}

async function renderTokens(balances) {
  tokenList = [];
  if (!balances.length) {
    el('tokens').innerHTML = '<span class="muted">No tokens.</span>';
    return;
  }
  el('tokens').innerHTML = '<span class="muted">loading details…</span>';
  for (const b of balances) {
    let name = '', ticker = '', decimals = 0;
    try {
      const d = await jget(`${NODE_URL}/assets/details/${b.assetId}`);
      name = d.name || ''; ticker = d.ticker || ''; decimals = d.decimals || 0;
    } catch { /* ignore */ }
    tokenList.push({ id: b.assetId, name, ticker, decimals, balance: b.balance });
  }
  tokenList.sort((a, b) => (a.name + a.ticker).localeCompare(b.name + b.ticker));
  if (!tokenList.length) { el('tokens').innerHTML = '<span class="muted">No tokens.</span>'; return; }
  el('tokens').innerHTML =
    '<table class="t-tbl"><thead><tr><th>Token</th><th>Balance</th></tr></thead><tbody>' +
    tokenList
      .map(
        (t) =>
          `<tr><td>${(t.ticker || t.name || t.id.slice(0, 8)).trim()}</td><td>${fmt(t.balance, t.decimals)}</td></tr>`,
      )
      .join('') +
    '</tbody></table>';
}

function populateAssetDropdown() {
  const sel = el('send-asset');
  const prev = sel.value;
  sel.innerHTML = '<option value="WAVES">WAVES</option>' +
    tokenList
      .map((t) => `<option value="${t.id}">${(t.ticker || t.name || t.id.slice(0, 12)).trim()}</option>`)
      .join('');
  sel.value = prev || 'WAVES';
}

function renderLeases(leases) {
  if (!leases.length) {
    el('leases').innerHTML = '<span class="muted">No active leases.</span>';
    return;
  }
  el('leases').innerHTML =
    '<table class="t-tbl"><thead><tr><th>Amount</th><th>Node</th><th></th></tr></thead><tbody>' +
    leases
      .map(
        (l, i) =>
          `<tr><td>${fmt(l.amount)} WAVES</td><td class="mono">${l.recipient.slice(0, 14)}…</td>` +
          `<td><button class="ghost tiny cancel" data-id="${l.id}" data-idx="${i}">Cancel</button></td></tr>`,
      )
      .join('') +
    '</tbody></table>';
  document.querySelectorAll('.cancel').forEach((b) =>
    b.addEventListener('click', () => doCancelLease(b.dataset.id)),
  );
}

/* ---------------- sign + broadcast helper ---------------- */
async function signAndBroadcast(tx, signOpts) {
  const bytes = serialize(tx);
  showResult('➜ Confirm the transaction on your Ledger device…');
  const signature = await ledger.signTransaction(current.path(), bytes, signOpts);
  tx.proofs = [signature];
  if (!verify(tx)) throw new Error('Signature verification failed locally — not broadcast.');
  showResult('✓ Signature verified. Broadcasting…');
  const res = await jpost(`${NODE_URL}/transactions/broadcast`, tx);
  return res;
}
current.path = () => pathForAccount(current.index);

/* ---------------- send ---------------- */
async function doSend() {
  clearMsg();
  const recipient = el('send-recipient').value.trim();
  const amountStr = el('send-amount').value.trim();
  const assetId = el('send-asset').value;
  if (!recipient) return showError('Enter a recipient address.');
  if (!amountStr && amountStr !== '0') return showError('Enter an amount.');

  try {
    let amountUnits; let amountPrecision = 8; let label = 'WAVES';
    if (assetId === 'WAVES') {
      const eff = await jget(`${NODE_URL}/addresses/effectiveBalance/${current.address}`);
      const spendable = eff.balance;
      amountUnits = amountStr.toUpperCase() === 'MAX' ? spendable - TRANSFER_FEE : Math.round(parseFloat(amountStr) * FRACTION);
      if (amountUnits + TRANSFER_FEE > spendable) throw new Error(`Insufficient spendable balance (need ${fmt(amountUnits + TRANSFER_FEE)} WAVES incl. fee).`);
    } else {
      const t = tokenList.find((x) => x.id === assetId);
      if (!t) throw new Error('Token not found.');
      label = (t.ticker || t.name || assetId.slice(0, 8)).trim();
      amountPrecision = t.decimals;
      amountUnits = amountStr.toUpperCase() === 'MAX' ? t.balance : Math.round(parseFloat(amountStr) * 10 ** t.decimals);
      const waves = await jget(`${NODE_URL}/addresses/balance/${current.address}`);
      if (TRANSFER_FEE > waves.balance) throw new Error('Not enough WAVES to pay the fee.');
    }
    if (!Number.isFinite(amountUnits) || amountUnits <= 0) throw new Error('Invalid amount.');

    const tx = transfer({
      recipient,
      amount: amountUnits,
      assetId: assetId === 'WAVES' ? null : assetId,
      fee: TRANSFER_FEE,
      feeAssetId: null,
      senderPublicKey: current.publicKey,
      timestamp: Date.now(),
      version: 2,
    });
    const res = await signAndBroadcast(tx, { dataType: 4, dataVersion: 2, amountPrecision, feePrecision: 8 });
    showResult(
      `✅ Sent ${fmt(amountUnits, amountPrecision)} ${label} to ${recipient.slice(0, 14)}…` +
      `<br/>TX: <a href="https://wavesexplorer.com/tx/${res.id}" target="_blank">${res.id.slice(0, 20)}…</a>`,
    );
    setTimeout(loadAccount, 4000);
  } catch (e) {
    showError(e.message || String(e));
  }
}

function doMax() {
  const assetId = el('send-asset').value;
  if (assetId === 'WAVES') el('send-amount').value = 'MAX';
  else {
    const t = tokenList.find((x) => x.id === assetId);
    if (t) el('send-amount').value = fmt(t.balance, t.decimals);
  }
}

/* ---------------- lease ---------------- */
async function doLease() {
  clearMsg();
  const node = el('lease-node').value.trim();
  const amountStr = el('lease-amount').value.trim();
  if (!node) return showError('Enter a node address.');
  if (!amountStr) return showError('Enter an amount.');
  try {
    const eff = await jget(`${NODE_URL}/addresses/effectiveBalance/${current.address}`);
    const amountUnits = Math.round(parseFloat(amountStr) * FRACTION);
    if (amountUnits + TRANSFER_FEE > eff.balance) throw new Error('Insufficient spendable WAVES (incl. fee).');
    const tx = leaseTx({
      recipient: node,
      amount: amountUnits,
      chainId: 87,
      fee: TRANSFER_FEE,
      senderPublicKey: current.publicKey,
      timestamp: Date.now(),
      version: 2,
    });
    const res = await signAndBroadcast(tx, { dataType: 8, dataVersion: 2, amountPrecision: 8, feePrecision: 8 });
    showResult(
      `✅ Leased ${fmt(amountUnits)} WAVES to ${node.slice(0, 14)}…` +
      `<br/>TX: <a href="https://wavesexplorer.com/tx/${res.id}" target="_blank">${res.id.slice(0, 20)}…</a>`,
    );
    setTimeout(loadAccount, 4000);
  } catch (e) {
    showError(e.message || String(e));
  }
}

async function doCancelLease(leaseId) {
  clearMsg();
  try {
    const tx = cancelLeaseTx({
      leaseId,
      chainId: 87,
      fee: TRANSFER_FEE,
      senderPublicKey: current.publicKey,
      timestamp: Date.now(),
      version: 2,
    });
    const res = await signAndBroadcast(tx, { dataType: 9, dataVersion: 2, amountPrecision: 0, feePrecision: 8 });
    showResult(
      `✅ Cancelled lease ${leaseId.slice(0, 16)}…` +
      `<br/>TX: <a href="https://wavesexplorer.com/tx/${res.id}" target="_blank">${res.id.slice(0, 20)}…</a>`,
    );
    setTimeout(loadAccount, 4000);
  } catch (e) {
    showError(e.message || String(e));
  }
}

/* ---------------- wire up ---------------- */
el('connect-btn').addEventListener('click', connect);
el('refresh-btn').addEventListener('click', loadAccount);
el('account-input').addEventListener('change', loadAccount);
el('send-btn').addEventListener('click', doSend);
el('send-max').addEventListener('click', doMax);
el('lease-btn').addEventListener('click', doLease);
el('send-asset').addEventListener('change', () => { if (el('send-amount').value === 'MAX') doMax(); });

/* ---------------- copy donation address ---------------- */
el('copy-donate').addEventListener('click', async () => {
  try {
    await navigator.clipboard.writeText('3P5FinomNpMBzhs8Vs5kCGBMcXHwLAkvAQv');
    const btn = el('copy-donate');
    const prev = btn.textContent;
    btn.textContent = 'Copied ✓';
    btn.disabled = true;
    setTimeout(() => { btn.textContent = prev; btn.disabled = false; }, 1500);
  } catch {
    showError('Could not copy automatically — select the address and copy manually.');
  }
});
